import { useState } from "react";
import { Heart, Megaphone, Gift, Handshake, Users, HeartHandshake, Sparkles, Globe, ChevronDown, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow } from "@/components/Motion";
import { apiPost } from "@/lib/brand";

const PATHWAYS = [
    { id: "supporter", icon: Heart, title: "Supporters", copy: "Share the mission, show up to drops, and put the Network in front of people who need it." },
    { id: "sponsor", icon: Megaphone, title: "Sponsors", copy: "Put your brand behind content, events, and community programs with real reach." },
    { id: "donor", icon: Gift, title: "Donors", copy: "Fuel operations today and the future nonprofit vision tomorrow. Note: not tax-deductible yet." },
    { id: "partner", icon: Handshake, title: "Partners", copy: "Organizations and brands building programs, events, and opportunities alongside us." },
    { id: "collaborator", icon: Users, title: "Collaborators", copy: "Cross-lane projects: your craft plus the Network's structure and audience." },
    { id: "volunteer", icon: HeartHandshake, title: "Volunteers", copy: "Time and skill for outreach, events, and youth support as programs roll out." },
    { id: "creator", icon: Sparkles, title: "Creators", copy: "Bring your idea, get structure and direction, and build your lane inside the Network." },
    { id: "community-member", icon: Globe, title: "Community Members", copy: "From any walk of life — if you're willing to do the work, there's a place for you." },
];

const FAQ = [
    { q: "Is PURP ALERT NETWORK a nonprofit?", a: "No. PURP ALERT NETWORK is a business (LLC) being built today. PURP ALERT INC. is a separate, future nonprofit vision — it is not yet formed and is not a 501(c)(3)." },
    { q: "Are donations or contributions tax-deductible?", a: "No. Support given today funds PURP ALERT NETWORK LLC operations and is not tax-deductible. When the future nonprofit becomes real, we will say so clearly." },
    { q: "Do I need experience to join as a creator?", a: "No. You need an idea and the willingness to do the work. Structure, accountability, and creative direction are what the Network exists to provide." },
    { q: "What exactly is MY 5?", a: "The signature PURP ALERT content concept — intentionally not \"Top 5.\" Five of whatever the creator feels like sharing that day: facts, hot takes, faith, music, film, storytimes, journals, art, fashion, gaming, or live." },
    { q: "How do sponsors and partners get started?", a: "Fill out the form below with the sponsor or partner pathway. Tell us what you do and what you want to build — the Network will reach out directly." },
];

function FaqItem({ q, a, i }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-2xl bg-panel/75 border border-white/10 overflow-hidden" data-testid={`faq-item-${i}`}>
            <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                onClick={() => setOpen(!open)}
                data-testid={`faq-toggle-${i}`}
                aria-expanded={open}
            >
                <span className="font-display font-bold uppercase text-lg md:text-xl">{q}</span>
                <ChevronDown className={`w-5 h-5 text-purp-neon shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">{a}</p>
                </div>
            </div>
        </div>
    );
}

export default function GetInvolved() {
    const [form, setForm] = useState({ name: "", email: "", pathway: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.pathway) {
            toast.error("Name, email, and a pathway are required.");
            return;
        }
        setSending(true);
        try {
            await apiPost("/get-involved", { ...form, name: form.name.trim(), email: form.email.trim() });
            toast.success("You're in the Network. PURP ALERT will reach out soon.");
            setForm({ name: "", email: "", pathway: "", message: "" });
        } catch {
            toast.error("Something glitched. Try again in a moment.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors";

    return (
        <div data-testid="get-involved-page">
            <PageHero
                eyebrow="Get Involved"
                titleLines={["There's a lane", "with your name on it."]}
                copy="Supporters, sponsors, donors, partners, collaborators, volunteers, creators, community members — pick your pathway and pull up."
            />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="pathways-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">Pathways</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Eight ways <span className="italic text-purp">in.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {PATHWAYS.map((p, i) => (
                            <FadeIn key={p.id} delay={0.05 * i}>
                                <button
                                    onClick={() => {
                                        setForm({ ...form, pathway: p.id });
                                        document.getElementById("involve-form")?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    className={`h-full w-full text-left rounded-2xl bg-panel/75 backdrop-blur-md border p-6 transition-colors duration-300 ${form.pathway === p.id ? "border-purp card-glow" : "border-white/10 hover:border-purp/50"}`}
                                    data-testid={`pathway-card-${p.id}`}
                                >
                                    <p.icon className="w-6 h-6 text-purp-neon" />
                                    <h3 className="mt-6 font-display font-bold uppercase text-xl">{p.title}</h3>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.copy}</p>
                                </button>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section id="involve-form" className="py-16 md:py-24 border-t border-white/10" data-testid="involve-form-section">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">Contact PURP ALERT</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Say it <span className="italic text-purp">here.</span>
                        </h2>
                        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="get-involved-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="gi-name" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Name *</label>
                                    <input id="gi-name" data-testid="get-involved-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} required />
                                </div>
                                <div>
                                    <label htmlFor="gi-email" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Email *</label>
                                    <input id="gi-email" type="email" data-testid="get-involved-email-input" className={inputCls} placeholder="you@yourlane.com" value={form.email} onChange={set("email")} required />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="gi-pathway" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Pathway *</label>
                                <select id="gi-pathway" data-testid="get-involved-pathway-select" className={inputCls} value={form.pathway} onChange={set("pathway")} required>
                                    <option value="" disabled>Choose your pathway</option>
                                    {PATHWAYS.map((p) => (
                                        <option key={p.id} value={p.id}>{p.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="gi-message" className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">Message</label>
                                <textarea id="gi-message" rows={5} data-testid="get-involved-message-input" className={inputCls} placeholder="Your idea, your craft, or how you want to help build." value={form.message} onChange={set("message")} />
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="get-involved-form-submit"
                                className="inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)] disabled:opacity-60"
                            >
                                {sending ? "Sending..." : "Send It"} <Send className="w-4 h-4" />
                            </button>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 leading-relaxed">
                                PURP ALERT NETWORK is a business (LLC). Contributions today support operations and are not tax-deductible.
                            </p>
                        </form>
                    </FadeIn>
                </div>
            </section>

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="faq-section">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">FAQ</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Asked <span className="italic text-purp">&amp; answered.</span>
                        </h2>
                        <div className="mt-10 space-y-4">
                            {FAQ.map((f, i) => (
                                <FaqItem key={i} q={f.q} a={f.a} i={i} />
                            ))}
                        </div>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
